import { useEffect, useRef } from 'react';

import { setAppStore } from '@/store';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import getScreenType from '@/utils/analytics/getScreenType';
import getCurrentUserId from '@/utils/user/getCurrentUserId';
import { AiGenScreenAction } from '@/types/analytics';

import {
  loadAnnouncedClaimId,
  saveAnnouncedClaimId,
} from '../storage/claimAnnouncementStorage';
import useFreePassEnabled from './useFreePassEnabled';
import useStreakState from './useStreakState';

/**
 * Auto-opens the sheet once per claimable day. Signed-in only: a guest reaches
 * the pitch by tapping Claim, and the guard key below is built from the user id
 * — `''` for a guest, so persisting it would write anonymous streak state to
 * device storage, which this feature must not do.
 */
const useStreakAutoOpen = () => {
  const enabled = useFreePassEnabled();
  const { streak, promo, isGuest, isReady } = useStreakState();
  const announcedRef = useRef<string | null>(null);

  useEffect(() => {
    const isClaimReady =
      enabled && !isGuest && isReady && streak.canClaim && Boolean(promo);
    if (!isClaimReady || !promo) return;

    const claimId = `${getCurrentUserId()}:${promo.id}:${
      streak.currentDayNumber
    }`;
    const wasAnnounced =
      announcedRef.current === claimId || loadAnnouncedClaimId() === claimId;
    if (wasAnnounced) return;

    announcedRef.current = claimId;
    saveAnnouncedClaimId(claimId);
    setAppStore({ isStreakSheetOpen: true });
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.STREAK_AUTO_OPEN,
      screen_type: getScreenType(),
    });
  }, [
    enabled,
    isGuest,
    isReady,
    promo,
    streak.canClaim,
    streak.currentDayNumber,
    streak.sheetMode,
  ]);
};

export default useStreakAutoOpen;
